<template>
  <div class="container-fluid px-0">
    <div class="card mb-3 mt-3 mx-2 mx-md-4">
      <!-- Card Header -->
      <div class="card-header p-0 position-relative mt-n3 z-index-2" style="height: 55px;">
        <div class="bg-primary shadow-primary border-radius-lg pt-3 pb-2 h-100">
          <div class="d-flex justify-content-between align-items-center px-3">
            <h6 class="text-white text-capitalize mb-0" style="font-size: clamp(0.9rem, 3vw, 1.1rem);">
{{ $t('academicDashboard.statistics') }}            </h6>
          </div>
        </div>
      </div>

      <div class="card-body px-0 pb-0"> 
        <!-- Loading State -->
        <div v-if="isLoading" class="text-center py-4">
          <div class="spinner-border text-primary" style="width: 2rem; height: 2rem;" role="status">
            <span class="visually-hidden">{{ $t('adminDashboard.loadingData') }}</span>
          </div>
          <p class="mt-2 mb-0">{{ $t('adminDashboard.loadingData') }}</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="alert alert-danger mx-3 mx-md-4 mb-3 py-2">
          <i class="fas fa-exclamation-triangle me-2"></i> {{ error }}
        </div>

        <!-- Main Content -->
        <div v-else>
          <!-- Welcome Message -->
          <div class="alert alert-primary mx-3 mx-md-4 mt-2 mb-3 py-2">
            <h5 class="alert-heading mb-1" style="font-size: clamp(1rem, 3.5vw, 1.25rem);">
              {{ $t('academicDashboard.welcome') }} {{ userName }}!
            </h5>
            <p class="mb-0" style="font-size: clamp(0.8rem, 2.5vw, 0.95rem);">
              {{ $t('academicDashboard.welcomeSubtitle') }}            </p>
          </div>

          <!-- Stats Cards Row -->
          <div class="row g-2 mb-4 mx-1 mx-md-3">
            <!-- Classes Card -->
            <div class="col-12 col-sm-6 col-xl-3 mb-2 mb-xl-0">
              <div class="card border-left-primary shadow h-100 py-2">
                <div class="card-body">
                  <div class="row no-gutters align-items-center">
                    <div class="col mr-2">
                      <div class="text-xs font-weight-bold text-primary text-uppercase mb-1">
{{ $t('adminDashboard.totalClasses') }}                      </div>
                      <div class="h5 mb-0 font-weight-bold text-gray-800">
                        {{ stats.classes_count || 0 }}
                      </div>
                      <div class="text-xs text-muted mt-1">
                        <i class="fas fa-check-circle me-1"></i> {{ stats.active_classes_count || 0 }} {{ $t('academicDashboard.activeClasses') }}
                      </div>
                    </div>
                    <div class="col-auto">
                      <i class="fas fa-chalkboard-teacher fa-2x text-gray-300"></i>
                    </div>
                  </div>
                  <router-link :to="{ name: 'classes.index' }" class="small text-primary text-decoration-none d-block mt-2">
                    {{ $t('adminDashboard.viewAll') }}
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Teachers Card -->
            <div class="col-12 col-sm-6 col-xl-3 mb-2 mb-xl-0">
              <div class="card border-left-success shadow h-100 py-2">
                <div class="card-body">
                  <div class="row no-gutters align-items-center">
                    <div class="col mr-2">
                      <div class="text-xs font-weight-bold text-success text-uppercase mb-1">
{{ $t('adminDashboard.totalTeachers') }}                      </div>
                      <div class="h5 mb-0 font-weight-bold text-gray-800">
                        {{ stats.teachers_count || 0 }}
                      </div>
                      <div class="text-xs text-muted mt-1">
                        <i class="fas fa-male me-1"></i> {{ stats.male_teachers_count || 0 }} {{ $t('adminDashboard.maleTeachers') }}
                      </div>
                    </div>
                    <div class="col-auto">
                      <i class="fa-solid fa-person-chalkboard fa-2x text-gray-300"></i>
                    </div>
                  </div>
                  <router-link :to="{ name: 'teachers.index' }" class="small text-success text-decoration-none d-block mt-2">
                    {{ $t('adminDashboard.viewAll') }}
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Subjects Card -->
            <div class="col-12 col-sm-6 col-xl-3 mb-2 mb-xl-0">
              <div class="card border-left-info shadow h-100 py-2">
                <div class="card-body">
                  <div class="row no-gutters align-items-center">
                    <div class="col mr-2">
                      <div class="text-xs font-weight-bold text-info text-uppercase mb-1">
{{ $t('adminDashboard.totalSubjects') }}                      </div>
                      <div class="h5 mb-0 font-weight-bold text-gray-800">
                        {{ stats.subjects_count || 0 }}
                      </div>
                      <div class="text-xs text-muted mt-1">
                        <i class="fas fa-tasks me-1"></i> {{ stats.assignments_count || 0 }} {{ $t('adminDashboard.assignments') }}
                      </div>
                    </div>
                    <div class="col-auto">
                      <i class="fa-solid fa-book-open fa-2x text-gray-300"></i>
                    </div>
                  </div>
                  <router-link :to="{ name: 'subjects.index' }" class="small text-info text-decoration-none d-block mt-2">
                    {{ $t('adminDashboard.viewAll') }}
                  </router-link>
                </div>
              </div>
            </div>

            <!-- Teacher Attendance Card -->
            <div class="col-12 col-sm-6 col-xl-3 mb-2 mb-xl-0">
              <div class="card border-left-warning shadow h-100 py-2">
                <div class="card-body">
                  <div class="row no-gutters align-items-center">
                    <div class="col mr-2">
                      <div class="text-xs font-weight-bold text-warning text-uppercase mb-1">
{{ $t('academicDashboard.teacherAttendanceToday') }}                      </div>
                      <div class="h5 mb-0 font-weight-bold text-gray-800">
                        {{ stats.teacher_attendance_today.present || 0 }}/{{ stats.teacher_attendance_today.total || 0 }}
                        <span class="text-sm" :class="{
                          'text-success': attendancePercentage >= 80,
                          'text-warning': attendancePercentage >= 50 && attendancePercentage < 80,
                          'text-danger': attendancePercentage < 50
                        }">
                          ({{ attendancePercentage }}%)
                        </span>
                      </div>
                      <div class="text-xs text-muted mt-1">
                        <i class="fas fa-calendar me-1"></i> {{ formatDate(currentDate) }}
                      </div>
                    </div>
                    <div class="col-auto">
                      <i class="fas fa-calendar-check fa-2x text-gray-300"></i>
                    </div>
                  </div>
                  <router-link :to="{ name: 'teacher-attendances.index' }" class="small text-warning text-decoration-none d-block mt-2">
                    {{ $t('adminDashboard.viewAll') }}
                  </router-link>
                </div>
              </div>
            </div>
          </div>

          <div class="row g-2 mx-1 mx-md-3">
            <div class="col-12 col-lg-12 mb-2">
              <div class="card shadow h-100">
                <div class="card-header bg-gradient-primary py-3">
                  <h6 class="m-0 font-weight-bold text-white mx-2" style="font-size: clamp(0.9rem, 3vw, 1rem);">{{ $t('adminDashboard.quickActions') }}</h6>
                </div>
                <div class="card-body p-2">
                  <div class="row g-2">
                    <div class="col-6 col-md-4 col-lg-2 mb-2">
                      <router-link 
                        :to="{ name: 'classes.index' }" 
                        class="btn btn-outline-primary w-100 h-100 py-2 d-flex flex-column align-items-center justify-content-center"
                        style="min-height: 100px;"
                      >
                        <i class="fas fa-chalkboard-teacher mb-2" style="font-size: 1.5rem;"></i>
                        <span style="font-size: clamp(0.7rem, 2vw, 0.85rem);">{{ $t('academicDashboard.manageClasses') }}</span>
                      </router-link>
                    </div>
                    
                    <div class="col-6 col-md-4 col-lg-2 mb-2">
                      <router-link 
                        :to="{ name: 'teachers.index' }" 
                        class="btn btn-outline-primary w-100 h-100 py-2 d-flex flex-column align-items-center justify-content-center"
                        style="min-height: 100px;"
                      >
                        <i class="fa-solid fa-person-chalkboard mb-2" style="font-size: 1.5rem;"></i>
                        <span style="font-size: clamp(0.7rem, 2vw, 0.85rem);">{{ $t('academicDashboard.manageTeachers') }}</span>
                      </router-link>
                    </div>
                    
                    <!-- Manage Subjects -->
                    <div class="col-6 col-md-4 col-lg-2 mb-2">
                      <router-link 
                        :to="{ name: 'subjects.index' }" 
                        class="btn btn-outline-primary w-100 h-100 py-2 d-flex flex-column align-items-center justify-content-center"
                        style="min-height: 100px;"
                      >
                        <i class="fa-solid fa-book-open mb-2" style="font-size: 1.5rem;"></i>
                        <span style="font-size: clamp(0.7rem, 2vw, 0.85rem);">{{ $t('academicDashboard.manageSubjects') }}</span>
                      </router-link>
                    </div>
                    
                    <!-- Exams Schedule -->
                    <div class="col-6 col-md-4 col-lg-2 mb-2">
                      <router-link 
                        :to="{ name: 'exams.management' }" 
                        class="btn btn-outline-primary w-100 h-100 py-2 d-flex flex-column align-items-center justify-content-center"
                        style="min-height: 100px;"
                      >
                        <i class="fas fa-calendar-days mb-2" style="font-size: 1.5rem;"></i>
                        <span style="font-size: clamp(0.7rem, 2vw, 0.85rem);">{{ $t('academicDashboard.examsSchedule') }}</span>
                      </router-link>
                    </div>
                    
                    <!-- Grade Sheets -->
                    <div class="col-6 col-md-4 col-lg-2 mb-2">
                      <router-link 
                        :to="{ name: 'grade-sheets.index' }" 
                        class="btn btn-outline-primary w-100 h-100 py-2 d-flex flex-column align-items-center justify-content-center"
                        style="min-height: 100px;"
                      >
                        <i class="fas fa-file-alt mb-2" style="font-size: 1.5rem;"></i>
                        <span style="font-size: clamp(0.7rem, 2vw, 0.85rem);">{{ $t('academicDashboard.gradesSheets') }}</span>
                      </router-link>
                    </div>
                    
                    <!-- Teacher Attendance -->
                    <div class="col-6 col-md-4 col-lg-2 mb-2">
                      <router-link 
                        :to="{ name: 'teacher-attendances.index' }" 
                        class="btn btn-outline-primary w-100 h-100 py-2 d-flex flex-column align-items-center justify-content-center"
                        style="min-height: 100px;"
                      >
                        <i class="fas fa-calendar-check mb-2" style="font-size: 1.5rem;"></i>
                        <span style="font-size: clamp(0.7rem, 2vw, 0.85rem);">{{ $t('academicDashboard.teacherAttendance') }}</span>
                      </router-link>
                    </div>
                  </div>
                </div>
              </div>
            </div>

         <!-- Teacher Attendance Chart -->
  <div class="col-12 mb-4">
    <div class="card shadow">
      <div class="card-header bg-gradient-primary py-3">
        <h6 class="m-0 font-weight-bold text-white mx-2">{{ $t('academicDashboard.teacherAttendanceStats') }}</h6>
      </div>
      <div class="card-body p-2">
        <div style="height: 250px;">
          <canvas ref="attendanceChartRef" height="250"></canvas>
        </div>
      </div>
    </div>
  </div>

  <!-- Subject Distribution Chart -->
  <div class="col-12 mb-4">
    <div class="card shadow">
      <div class="card-header bg-gradient-primary py-3">
        <h6 class="m-0 font-weight-bold text-white mx-2">{{ $t('academicDashboard.subjectDistribution') }}</h6>
      </div>
      <div class="card-body p-2">
        <div style="height: 250px;">
          <canvas ref="studentsByClassChartRef" height="250"></canvas>
        </div>
      </div>
    </div>
  </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script setup>
import { ref, onMounted, nextTick, computed, onUnmounted } from 'vue';
import { useStore } from 'vuex';
import Chart from 'chart.js/auto';
import api from '@/api';
import Cookies from "js-cookie";
import { useI18n } from 'vue-i18n';
const { t } = useI18n();
const store = useStore();
const isLoading = ref(true);
const error = ref(null);
const attendanceChartRef = ref(null);
const studentsByClassChartRef = ref(null);
const currentDate = ref(new Date().toISOString().split('T')[0]);

// Chart instances
const attendanceChartInstance = ref(null);
const studentsByClassChartInstance = ref(null);

// State initialization
const stats = ref({
  classes_count: 0,
  active_classes_count: 0,
  teachers_count: 0,
  male_teachers_count: 0,
  subjects_count: 0,
  assignments_count: 0,
  teacher_attendance_today: { present: 0, total: 0, percentage: 0 },
  subject_distribution: { labels: [], datasets: [] } 
});

// Computed properties
const userName = computed(() => store.state.user?.first_name || Cookies.get('user_name') || 'مسؤول أكاديمي');
const attendancePercentage = computed(() => stats.value.teacher_attendance_today.percentage || 0);

// Format date
const formatDate = (dateString) => {
  if (!dateString) return 'غير محدد';
  const options = { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric',
  };
  return new Date(dateString).toLocaleDateString('ar', options);
};

const initCharts = async () => {
  await nextTick();
  
  // Check if canvas elements exist
  if (!attendanceChartRef.value || !studentsByClassChartRef.value) {
    console.error('Canvas elements not found');
    return;
  }

  // Destroy existing charts if they exist
  if (attendanceChartInstance.value) {
    attendanceChartInstance.value.destroy();
  }
  if (studentsByClassChartInstance.value) {
    studentsByClassChartInstance.value.destroy();
  }

  // Initialize Teacher Attendance Chart (Bar Chart)
  const attendanceCtx = attendanceChartRef.value.getContext('2d');
  attendanceChartInstance.value = new Chart(attendanceCtx, {
    type: 'bar',
    data: {
      labels: [  t('adminDashboard.present'),  // "حضور"
  t('adminDashboard.absent') ],
      datasets: [{
  label: t('adminDashboard.teacherAttendance'),
          data: [
          stats.value.teacher_attendance_today.present || 0,
          (stats.value.teacher_attendance_today.total || 0) - (stats.value.teacher_attendance_today.present || 0)
        ],
        backgroundColor: ['#4e73df', '#e74a3b'],
        borderColor: ['#4e73df', '#e74a3b'],
        borderWidth: 1
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top',
          rtl: true,
          labels: {
            font: {
              family: 'Tahoma, Arial, sans-serif'
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1,
            mirror: true
          }
        },
        x: {
          ticks: {
            mirror: true
          }
        }
      }
    }
  });

  // Initialize Subject Distribution Chart (Doughnut Chart) with real data
  const subjectCtx = studentsByClassChartRef.value.getContext('2d');
  studentsByClassChartInstance.value = new Chart(subjectCtx, {
    type: 'doughnut',
    data: {
      labels: stats.value.subject_distribution.labels,
      datasets: [{
        label: stats.value.subject_distribution.datasets[0]?.label || 'عدد الصفوف',
        data: stats.value.subject_distribution.datasets[0]?.data || [],
        backgroundColor: stats.value.subject_distribution.datasets[0]?.backgroundColor || [
          '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'
        ],
        borderWidth: 1
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'right',
          rtl: true,
          labels: {
            font: {
              family: 'Tahoma, Arial, sans-serif'
            },
            padding: 20
          }
        }
      },
      cutout: '70%'
    }
  });
};

const fetchDashboardData = async () => {
  try {
    isLoading.value = true;
    error.value = null;
    
    const [statsRes, attendanceTodayRes, subjectRes] = await Promise.all([
      api.get('/academic-dashboard/stats'),
      api.get('/academic-dashboard/teacher-attendance-today'),
      api.get('/academic-dashboard/subject-distribution')
    ]);

    stats.value = {
      ...statsRes.data.data,
      teacher_attendance_today: attendanceTodayRes.data.data || {
        present: 0,
        total: 0,
        percentage: 0
      },
      subject_distribution: subjectRes.data.data || {
        labels: [],
        datasets: []
      }
    };
    
    // Initialize charts after data is loaded
    await initCharts();

  } catch (err) {
    console.error('Dashboard error:', err);
    error.value = 'فشل تحميل بيانات لوحة التحكم. يرجى المحاولة مرة أخرى.';
    
    // Initialize charts with default data
    await initCharts();
  } finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await fetchDashboardData();
  nextTick();
  initCharts();
});

onUnmounted(() => {
  if (attendanceChartInstance.value) {
    attendanceChartInstance.value.destroy();
  }
  if (studentsByClassChartInstance.value) {
    studentsByClassChartInstance.value.destroy();
  }
});
</script>
<style scoped>
/* Base Styles */
.card {
  border-radius: 0.5rem;
  box-shadow: 0 0.15rem 1.75rem 0 rgba(58, 59, 69, 0.15);
  transition: all 0.3s;
}

.card:hover {
  transform: translateY(-5px);
  box-shadow: 0 0.5rem 2rem 0 rgba(58, 59, 69, 0.2);
}

.card-header {
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.border-left-primary {
  border-left: 0.25rem solid #9c27b0 !important;
}

.border-left-success {
  border-left: 0.25rem solid #2c91ae !important;
}

.border-left-info {
  border-left: 0.25rem solid #36b9cc !important;
}

.border-left-warning {
  border-left: 0.25rem solid #f6c23e !important;
}

.text-xs {
  font-size: 0.7rem;
}

.text-sm {
  font-size: 0.85rem;
}

.btn-outline-primary {
  transition: all 0.3s;
}

.btn-outline-primary:hover {
  color: white !important;
}

.text-gray-800 {
  color: #5a5c69 !important;
}

.text-gray-300 {
  color: #dddfeb !important;
}

/* Responsive Adjustments */
@media (max-width: 767.98px) {
  .card-header h6 {
    font-size: 0.9rem !important;
  }
  
  .card-body {
    padding: 0.75rem !important;
  }
  
  .quick-actions .btn {
    padding: 0.5rem !important;
    font-size: 0.75rem !important;
  }
  
  /* Adjust chart heights for mobile */
  .card-body > div[style*="height"] {
    height: 200px !important;
  }
}

@media (max-width: 575.98px) {
  .stats-card .col-12 {
    margin-bottom: 0.5rem;
  }
  
  /* Make quick action buttons full width on extra small screens */
  .quick-actions .col-6 {
    flex: 0 0 100%;
    max-width: 100%;
  }
  
  /* Adjust chart heights for very small screens */
  .card-body > div[style*="height"] {
    height: 180px !important;
  }
  
  /* Ensure text doesn't overflow in cards */
  .card-body {
    word-wrap: break-word;
  }
  
  /* Improve button touch targets on mobile */
  .btn {
    padding: 0.5rem 1rem;
    min-width: 44px;
    min-height: 44px;
  }
}

/* Ensure charts are responsive */
canvas {
  max-width: 100%;
  height: auto !important;
}

/* Better spacing for mobile */
.mx-1 {
  margin-left: 0.25rem !important;
  margin-right: 0.25rem !important;
}

/* Make sure images don't overflow */
img {
  max-width: 100%;
  height: auto;
}

/* Better alignment for RTL */
[dir="rtl"] .text-end {
  text-align: left !important;
}

[dir="rtl"] .text-start {
  text-align: right !important;
}
/* Ensure charts are properly sized */
.card-body > div[style*="height"] {
  position: relative;
  width: 100%;
}

canvas {
  width: 100% !important;
  height: 100% !important;
  display: block;
}

/* Better spacing for charts */
.card-body.p-2 {
  padding: 1rem !important;
}
</style>